import { defineStore } from "pinia";
import eventBus from "../../../utilities/eventBus";
import { useUserStore } from "./useUsersStore";

import { MessageService } from "../../../services/message-service";
import { Container } from "aurelia-dependency-injection";
import {
    IConversationNavOptions,
    IGetConversationResult
} from "../../../resources-vue/vue-interfaces/messaging-service/i-message";
import { ConversationSortEnum } from "../../../enums/ConversationSortEnum";
import { formatTaggedUserMessage, sortPlaceholdersList } from "../../../common/vue-helpers/modifiers/format-message";
import { ConversationTypeEnum } from "../../../enums/ConversationTypeEnum";
import { useMessagesStore } from "./useMessagesStore";
import { useApp } from "../../App/useApp";
import { useDraftsStore } from "./useDraftsStore";
import { useEditorStore } from "./useEditorStore";
import { useCallStore } from "./useCallStore";

interface IStoreConvPagination {
    currentPage: number;
    totalPageCount: number;
    isFetchingThreads: boolean;
}

export const useConversationStore = defineStore("conversations", {
    state() {
        return {
            activeConversationId: null,
            activeConvType: "conversation" as "conversation" | "newConversation" | "draftConversation",

            conversations: {} as Record<string, IConversationNavOptions>,
            orderedConvIds: [] as string[],

            // variables for search and sort filters
            showFilteredConvs: false as boolean,
            lastSelectedConv: {} as IConversationNavOptions,

            convPagination: {
                currentPage: 1,
                totalPageCount: null,
                isFetchingThreads: true
            } as IStoreConvPagination,

            unreadConversations: {},
            unreadConvIds: [] as string[],

            showChatTutorial: null as boolean
        };
    },

    getters: {
        getActiveConversation: (state) => {
            if (state.activeConversationId?.includes("draft")) {
                const draftStore = useDraftsStore();
                return draftStore.conversationDrafts[state.activeConversationId];
            }
            if (state.lastSelectedConv?.id === state.activeConversationId) {
                return state.lastSelectedConv;
            } else {
                return state.conversations[state.activeConversationId] ?? null;
            }
        },

        getAllConversationsList: (state) => {
            return state.orderedConvIds.map((id: string) => state.conversations[id]);
        },

        getAllUnreadConvs: (state) => {
            return state.unreadConvIds.map((id: string) => state.unreadConversations[id]);
        }
    },

    actions: {
        async setActiveConversationId(newConvId: string, saveDraft: boolean = true) {
            this.handleDraftSaveAndLoad(newConvId, saveDraft);

            this.activeConversationId = newConvId;
            this.lastSelectedConv = this.conversations[newConvId];
            // update active conv type
            if (!newConvId) {
                // newConvId is sent as null when user moves to other pages from connect
                this.activeConvType = "conversation";
            } else if (newConvId === "newConversation") {
                this.activeConvType = "newConversation";
            } else if (newConvId.includes("draft")) {
                this.activeConvType = "draftConversation";
            } else {
                this.activeConvType = "conversation";
                const conv = this.conversations[newConvId];

                if (conv?.conversationType === ConversationTypeEnum.GroupConversation) {
                    const callStore = useCallStore();
                    callStore.getConversationRoomStatus(newConvId);
                }
                const messagesStore = useMessagesStore();
                await messagesStore.fetchMessages(newConvId, 25);
                if (conv?.unread > 0) {
                    await this.markConversationAsRead(newConvId);
                }
            }
        },

        handleDraftSaveAndLoad(newConvId: string, saveDraft: boolean = true) {
            const draftsStore = useDraftsStore();
            const editorStore = useEditorStore();
            if (saveDraft) {
                if (this.activeConvType === "conversation" || this.activeConvType === "draftConversation") {
                    if (!editorStore.isMsgEmpty) {
                        // storing draft message
                        draftsStore.saveDraftMessage(this.activeConversationId, editorStore.editorMessage);
                    } else if (editorStore.isMsgEmpty && this.activeConversationId) {
                        // delete
                        draftsStore.removeDraftMessage(this.activeConversationId);
                    }
                    editorStore.resetEditorMessage();
                } else if (this.activeConvType === "newConversation") {
                    // save draft conversation when user move away from new chat
                    if (!editorStore.isMsgEmpty || editorStore.newChatTitle || editorStore.newChatUsers.length > 0) {
                        const draftId = `draft-${crypto.randomUUID()}`;
                        const userStore = useUserStore();
                        userStore.upsertUsers(editorStore.newChatUsers);

                        const loginIds = editorStore.newChatUsers.map((user) => user.loginId);

                        const groupDetails = {
                            id: draftId,
                            title: editorStore.newChatTitle,
                            text: "Draft",
                            unread: 0,
                            modifiedOn: new Date(),
                            loginIds,
                            users: editorStore.newChatUsers,
                            lastMessageId: null,
                            isImportant: false,
                            isCurrentUserTagged: false,
                            conversationType: ConversationTypeEnum.GroupConversation,
                            isDraftConversation: true,
                            isMuted: false
                        };
                        draftsStore.createDraftConversation(draftId, groupDetails, editorStore.editorMessage);
                        editorStore.resetEditorMessage();
                    }
                }
            } else {
                editorStore.resetEditorMessage();
            }

            // doesn't load drafts for new chat window or user moving to different page
            if (!newConvId || newConvId === "newConversation") return;

            // load draft messages
            const existingDraftMsg = draftsStore.getDraftMessage(newConvId);
            if (existingDraftMsg) {
                editorStore.setEditorMessage(existingDraftMsg);
            }
        },

        async fetchConversations(
            searchTerm: string,
            sortBy: ConversationSortEnum,
            actionType: "append" | "replace" = "append"
        ) {
            const messageService = Container.instance.get(MessageService);
            try {
                const response: IGetConversationResult = await messageService.getConversations({
                    page: this.convPagination.currentPage,
                    pageLength: 20,
                    term: searchTerm.trim(),
                    sort: sortBy,
                    fetchSingleConversationsOnly: false
                });
                this.convPagination.totalPageCount = response.pageCount;
                const usersStore = useUserStore();
                const appStore = useApp();
                const draftStore = useDraftsStore();
                if (actionType === "replace") {
                    this.conversations = {};
                    this.orderedConvIds = [];
                }

                response.items.forEach((conv) => {
                    // stores each conv users in userStore
                    usersStore.upsertUsers(conv.users);

                    const { taggedUsers } = sortPlaceholdersList(conv.lastMessage?.placeHoldersList);
                    const { text, showTagSignForMsg } = formatTaggedUserMessage(
                        conv.text,
                        taggedUsers,
                        appStore.loggedInUserDetails.loginId
                    );

                    const convUsers = conv.users?.filter((user) => {
                        if (user.isCurrentParticipant && appStore.loggedInUserDetails.userId !== user.userId) {
                            appStore.setLoggedInUserDetails({ ...appStore.loggedInUserDetails, userId: user.userId });
                        }
                        if (!user.isCurrentParticipant) return user;
                        else return false;
                    });

                    this.conversations[conv.id] = {
                        id: conv.id,
                        title: conv.title,
                        text: text,
                        conversationType: conv.conversationType,
                        loginIds: convUsers.map((u) => u.loginId),
                        unread: conv.unread,
                        lastMessageId: conv.lastMessage?.id,
                        modifiedOn: conv.modifiedOn,
                        isImportant: conv.lastMessage?.isImportant ?? false,
                        isCurrentUserTagged: showTagSignForMsg,
                        isMuted: conv.isMuted,
                        hasUserLeftChat: !conv.users?.some((user) => user.isCurrentParticipant),
                        hasDraftMessage: !!draftStore.getDraftMessage(conv.id)
                    };

                    if (!this.orderedConvIds.includes(conv.id) && actionType === "append") {
                        this.orderedConvIds.push(conv.id);
                    }
                });

                if (actionType === "replace") {
                    this.showFilteredConvs = true;
                    this.orderedConvIds = response.items.map((conv) => conv.id);
                }

                if (!this.activeConversationId && response.items.length) {
                    this.setActiveConversationId(response.items[0].id);
                    this.lastSelectedConv = this.conversations[response.items[0].id];
                }

                if (this.showChatTutorial === null) {
                    // when no local storage and initial load
                    if (this.orderedConvIds.length > 0) {
                        this.setShowChatTutorial(false);
                    } else {
                        this.setShowChatTutorial(true);
                    }
                }
            } catch (error) {
                console.error(error);
                throw error;
            }
        },

        addNewConversation(convDetails: any) {
            // when new conversation is created
            const usersStore = useUserStore();
            const appStore = useApp();
            const draftStore = useDraftsStore();
            // stores each conv users in userStore
            usersStore.upsertUsers(convDetails.users);

            const convUsers = convDetails.users?.filter((user) => !user.isCurrentParticipant);

            const { taggedUsers } = sortPlaceholdersList(convDetails.lastMessage?.placeHoldersList);
            const { text, showTagSignForMsg } = formatTaggedUserMessage(
                convDetails.text,
                taggedUsers,
                appStore.loggedInUserDetails.loginId
            );

            this.conversations[convDetails.id] = {
                id: convDetails.id,
                title: convDetails.title,
                text: text,
                conversationType: convDetails.conversationType,

                loginIds: convUsers.map((u) => u.loginId),
                unread: convDetails.unread,
                lastMessageId: convDetails.lastMessage?.id,
                modifiedOn: convDetails.modifiedOn,
                isImportant: convDetails.lastMessage?.isImportant ?? false,
                isCurrentUserTagged: showTagSignForMsg,
                isMuted: convDetails.isMuted,
                hasUserLeftChat: !convDetails.users?.some((user) => user.isCurrentParticipant),
                hasDraftMessage: !!draftStore.getDraftMessage(convDetails.id)
            };
            this.moveConversationToTop(convDetails.id);
        },

        async fetchConversationById(convId: string) {
            const messageService = Container.instance.get(MessageService);

            const { conversation } = await messageService.getConversationById(convId);

            this.addNewConversation(conversation);
        },

        moveConversationToTop(convId: string) {
            const remaining = this.orderedConvIds.filter((item) => item !== convId);
            this.orderedConvIds = [convId, ...remaining];
        },

        setConvPagination(currentPage: number) {
            this.convPagination.currentPage = currentPage;
        },

        async updateSideNavMessage(convId: string, message: any) {
            // update conversation and/or conversation msg in side panel
            // Scenarios: - receive msg in existing or new chat, send msg in chat
            // - Undo delete message for last conv message
            const draftStore = useDraftsStore();
            const appStore = useApp();
            let conversation = this.conversations[convId];

            if (!conversation) {
                // fetch conv if it's not in the list
                await this.fetchConversationById(convId);
                conversation = this.conversations[convId];
            }

            const { taggedUsers } = sortPlaceholdersList(message.placeHoldersList);
            const { text, showTagSignForMsg } = formatTaggedUserMessage(
                message.text,
                taggedUsers,
                appStore.loggedInUserDetails.loginId
            );

            this.conversations[convId] = {
                ...conversation,
                text: text,
                modifiedOn: message.modifiedOn,
                isImportant: message.isImportant,
                isCurrentUserTagged: showTagSignForMsg,
                lastMessageId: message.id,
                unread: message?.unread ? conversation.unread + 1 : conversation.unread,
                hasDraftMessage: !!draftStore.getDraftMessage(convId)
            };
        },
        updateConvDetails(convId: string, payload: any) {
            this.conversations[convId] = payload;

            if (this.lastSelectedConv?.id === convId) {
                this.lastSelectedConv = payload;
            }
        },

        async markConversationAsRead(conversationId: string) {
            const conversationObj = { conversationId };
            const messageService = Container.instance.get(MessageService);

            const msgStore = useMessagesStore();
            msgStore.markMessagesAsRead(conversationId);

            const conv = this.conversations[conversationId];
            if (conv) {
                conv.unread = 0;
            }
            try {
                await messageService.markConversationAsRead(conversationObj);
                if (this.unreadConvIds.includes(conversationId)) {
                    this.removeUnreadConversation(conversationId);
                }
            } catch (error) {
                console.error("Error marking conversation as read:", error);
            }
        },

        async updateConversationUsers(convId: string, actionType: "added" | "left" | "leave", userId: string = null) {
            // handles realtime user added or removed/left from the group
            const conv = this.conversations[convId];
            if (conv) {
                const userStore = useUserStore();

                if (actionType === "added") {
                    const messageService = Container.instance.get(MessageService);

                    const conversationUsers = await messageService.getConversationUsers(convId);
                    const users = conversationUsers.users.items.flatMap((user) => {
                        if (user.isCurrentParticipant) {
                            conv.hasUserLeftChat = false;
                            return [];
                        }
                        return user;
                    });
                    userStore.upsertUsers(users);
                    conv.loginIds = users.map((u) => u.loginId);
                } else if (actionType === "left") {
                    const loginId = userStore.getLoginIdByUserId(userId);
                    if (conv.loginIds.includes(loginId)) {
                        conv.loginIds = conv.loginIds.filter((id) => id !== loginId);
                    } else {
                        // current user was removed by someone else
                        conv.hasUserLeftChat = true;
                    }
                } else {
                    const loginId = userStore.getLoginIdByUserId(userId);
                    conv.loginIds = conv.loginIds.filter((id) => id !== loginId);
                    conv.hasUserLeftChat = true;
                }
            }
        },

        updateConversationTitle(convId: string, title: string) {
            const conv = this.conversations[convId];
            if (conv) {
                conv.title = title;
            }
        },

        deleteStoreConversation(convId: string) {
            const deletedIndex = this.orderedConvIds.findIndex((id: string) => id === convId);

            if (deletedIndex < 0 || deletedIndex >= this.orderedConvIds.length) {
                return;
            }

            let nextConversationId = null;

            // Determine the next conversation to select after deletion
            if (this.orderedConvIds.length > 1) {
                if (deletedIndex + 1 < this.orderedConvIds.length) {
                    nextConversationId = this.orderedConvIds[deletedIndex + 1];
                } else if (deletedIndex - 1 >= 0) {
                    nextConversationId = this.orderedConvIds[deletedIndex - 1];
                }
            }

            // Remove the deleted conversation from the list
            eventBus.emit("replaceRouterURL", true);
            delete this.conversations[convId];
            const remaining = this.orderedConvIds.filter((item) => item !== convId);
            this.orderedConvIds = [...remaining];
            this.setActiveConversationId(nextConversationId);
        },

        markAllConvAsRead() {
            this.orderedConvIds.forEach((convId) => (this.conversations[convId].unread = 0));
            this.unreadConversations = {};
            this.unreadConvIds = [];
        },

        saveLastSelectedConv(payload) {
            this.lastSelectedConv = payload;
        },

        clearFilteredData() {
            this.showFilteredConvs = false;
            this.convPagination.currentPage = 1;
        },

        // Unread msgs dropdown actions

        async getUnreadConversations() {
            // For unread messages dropdown
            const appStore = useApp();
            const messageService = Container.instance.get(MessageService);
            if (this.unreadConvIds.length < 1) {
                let result = await messageService.getUnreadConversations({
                    page: 1,
                    pageLength: 5
                });
                const convs = result.items ? result.items : [];

                for (const msg of convs) {
                    const userStore = useUserStore();
                    const convUsers = msg.users.filter((user) => !user.isCurrentParticipant);
                    userStore.upsertUsers(convUsers);
                    const { text, showTagSignForMsg } = formatTaggedUserMessage(
                        msg.text,
                        msg.placeHoldersList,
                        appStore.loggedInUserDetails.loginId
                    );
                    this.unreadConversations[msg.id] = {
                        ...msg,
                        text,
                        isCurrentUserTagged: showTagSignForMsg,
                        users: convUsers
                    };
                    this.unreadConvIds.push(msg.id);
                }
            }
        },

        addUnreadConversation(convId: string) {
            const conv = this.conversations[convId];
            const userStore = useUserStore();

            if (conv) {
                if (this.unreadConvIds.includes(convId)) {
                    this.unreadConversations[convId].unread = this.unreadConversations[convId].unread + 1;
                } else {
                    this.unreadConversations[convId] = { ...conv, users: userStore.getMultipleUsers(conv.loginIds) };
                    this.unreadConvIds.unshift(convId);
                }
            }
        },

        removeUnreadConversation(convId: string) {
            this.unreadConvIds = this.unreadConvIds.filter((id) => id !== convId);
        },

        // Chat tutorial actions

        setShowChatTutorial(value: boolean | null) {
            this.showChatTutorial = value;
            if (value === false) {
                localStorage.setItem("showConnectTutorial", "false");
            }
        }
    }
});
